import type { AppDataApi } from "../../api/AppDataApi";
import type { AmericanStateStaff } from "../../generated/dto/AmericanStateStaff";
import type { BoatTypeStaff } from "../../generated/dto/BoatTypeStaff";
import type { CountryStaff } from "../../generated/dto/CountryStaff";
import type { CreateAmericanState } from "../../generated/dto/CreateAmericanState";
import type { CreateBoatType } from "../../generated/dto/CreateBoatType";
import type { CreateCountry } from "../../generated/dto/CreateCountry";
import type { CreateMembershipTypeDiscount } from "../../generated/dto/CreateMembershipTypeDiscount";
import type { CreatePersonType } from "../../generated/dto/CreatePersonType";
import type { CreateSwimProofMethod } from "../../generated/dto/CreateSwimProofMethod";
import type { CreateTag } from "../../generated/dto/CreateTag";
import type { DeleteAmericanStateStaff } from "../../generated/dto/DeleteAmericanStateStaff";
import type { DeleteBoatTypeStaff } from "../../generated/dto/DeleteBoatTypeStaff";
import type { DeleteCountryStaff } from "../../generated/dto/DeleteCountryStaff";
import type { DeleteMembershipTypeDiscountStaff } from "../../generated/dto/DeleteMembershipTypeDiscountStaff";
import type { DeletePersonTypeStaff } from "../../generated/dto/DeletePersonTypeStaff";
import type { DeleteSwimProofMethodStaff } from "../../generated/dto/DeleteSwimProofMethodStaff";
import type { DeleteTagStaff } from "../../generated/dto/DeleteTagStaff";
import type { MembershipTypeDiscountFetchOptions } from "../../generated/dto/MembershipTypeDiscountFetchOptions";
import type { MembershipTypeDiscountStaff } from "../../generated/dto/MembershipTypeDiscountStaff";
import type { MembershipTypeFetchOptions } from "../../generated/dto/MembershipTypeFetchOptions";
import type { MembershipTypeStaff } from "../../generated/dto/MembershipTypeStaff";
import type { PersonTypeStaff } from "../../generated/dto/PersonTypeStaff";
import type { SwimProofMethodStaff } from "../../generated/dto/SwimProofMethodStaff";
import type { TagSearchParams } from "../../generated/dto/TagSearchParams";
import type { TagStaff } from "../../generated/dto/TagStaff";
import type { TagStaffSearchResult } from "../../generated/dto/TagStaffSearchResult";
import type { UpdateAmericanState } from "../../generated/dto/UpdateAmericanState";
import type { UpdateBoatType } from "../../generated/dto/UpdateBoatType";
import type { UpdateCountry } from "../../generated/dto/UpdateCountry";
import type { UpdateMembershipTypeDiscount } from "../../generated/dto/UpdateMembershipTypeDiscount";
import type { UpdatePersonType } from "../../generated/dto/UpdatePersonType";
import type { UpdateSwimProofMethod } from "../../generated/dto/UpdateSwimProofMethod";
import type { UpdateTag } from "../../generated/dto/UpdateTag";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class AppDataFrontendService {
  constructor(private readonly api: AppDataApi) {}
  private readonly personTypeStaffCache: Map<number, PersonTypeStaff> = new Map();
  private readonly tagStaffCache: Map<number, TagStaff> = new Map();
  private readonly boatTypeStaffCache: Map<number, BoatTypeStaff> = new Map();
  private readonly countryStaffCache: Map<number, CountryStaff> = new Map();
  private readonly americanStateStaffCache: Map<number, AmericanStateStaff> = new Map();
  private readonly swimProofMethodStaffCache: Map<number, SwimProofMethodStaff> = new Map();
  private readonly membershipTypeDiscountStaffCache: Map<number, MembershipTypeDiscountStaff> = new Map();
  async getPersonTypeStaff(id: number): Promise<PersonTypeStaff> {
    const __cached = this.personTypeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getPersonTypeStaff(id);
    this.personTypeStaffCache.set(id, __value);
    return __value
  }
  async getTagStaff(id: number): Promise<TagStaff> {
    const __cached = this.tagStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getTagStaff(id);
    this.tagStaffCache.set(id, __value);
    return __value
  }
  async getBoatTypeStaff(id: number): Promise<BoatTypeStaff> {
    const __cached = this.boatTypeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getBoatTypeStaff(id);
    this.boatTypeStaffCache.set(id, __value);
    return __value
  }
  async getCountryStaff(id: number): Promise<CountryStaff> {
    const __cached = this.countryStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCountryStaff(id);
    this.countryStaffCache.set(id, __value);
    return __value
  }
  async getAmericanStateStaff(id: number): Promise<AmericanStateStaff> {
    const __cached = this.americanStateStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getAmericanStateStaff(id);
    this.americanStateStaffCache.set(id, __value);
    return __value
  }
  async getSwimProofMethodStaff(id: number): Promise<SwimProofMethodStaff> {
    const __cached = this.swimProofMethodStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getSwimProofMethodStaff(id);
    this.swimProofMethodStaffCache.set(id, __value);
    return __value
  }
  async getMembershipTypeDiscountStaff(id: number, options?: MembershipTypeDiscountFetchOptions): Promise<MembershipTypeDiscountStaff> {
    const __cached = this.membershipTypeDiscountStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getMembershipTypeDiscountStaff(id, options);
    this.membershipTypeDiscountStaffCache.set(id, __value);
    return __value
  }
  async getAllPersonTypeStaffs(): Promise<Array<PersonTypeStaff>> {
    return this.api.getAllPersonTypeStaffs()
  }
  async getAllTagStaffs(): Promise<Array<TagStaff>> {
    return this.api.getAllTagStaffs()
  }
  async getAllBoatTypeStaffs(): Promise<Array<BoatTypeStaff>> {
    return this.api.getAllBoatTypeStaffs()
  }
  async getAllCountryStaffs(): Promise<Array<CountryStaff>> {
    return this.api.getAllCountryStaffs()
  }
  async getAllAmericanStateStaffs(): Promise<Array<AmericanStateStaff>> {
    return this.api.getAllAmericanStateStaffs()
  }
  async getAllSwimProofMethodStaffs(): Promise<Array<SwimProofMethodStaff>> {
    return this.api.getAllSwimProofMethodStaffs()
  }
  async getAllMembershipTypeDiscountStaffs(options?: MembershipTypeDiscountFetchOptions): Promise<Array<MembershipTypeDiscountStaff>> {
    return this.api.getAllMembershipTypeDiscountStaffs(options)
  }
  async createPersonTypeStaff(source: CreatePersonType): Promise<PersonTypeStaff> {
    const __result = await this.api.createPersonTypeStaff(source);
    this.personTypeStaffCache.clear();
    return __result
  }
  async createTagStaff(source: CreateTag): Promise<TagStaff> {
    const __result = await this.api.createTagStaff(source);
    this.tagStaffCache.clear();
    return __result
  }
  async createBoatTypeStaff(source: CreateBoatType): Promise<BoatTypeStaff> {
    const __result = await this.api.createBoatTypeStaff(source);
    this.boatTypeStaffCache.clear();
    return __result
  }
  async createCountryStaff(source: CreateCountry): Promise<CountryStaff> {
    const __result = await this.api.createCountryStaff(source);
    this.countryStaffCache.clear();
    return __result
  }
  async createAmericanStateStaff(source: CreateAmericanState): Promise<AmericanStateStaff> {
    const __result = await this.api.createAmericanStateStaff(source);
    this.americanStateStaffCache.clear();
    return __result
  }
  async createSwimProofMethodStaff(source: CreateSwimProofMethod): Promise<SwimProofMethodStaff> {
    const __result = await this.api.createSwimProofMethodStaff(source);
    this.swimProofMethodStaffCache.clear();
    return __result
  }
  async createMembershipTypeDiscountStaff(source: CreateMembershipTypeDiscount): Promise<MembershipTypeDiscountStaff> {
    const __result = await this.api.createMembershipTypeDiscountStaff(source);
    this.membershipTypeDiscountStaffCache.clear();
    return __result
  }
  async updatePersonTypeStaff(source: UpdatePersonType): Promise<PersonTypeStaff> {
    const __result = await this.api.updatePersonTypeStaff(source);
    this.personTypeStaffCache.clear();
    return __result
  }
  async updateTagStaff(source: UpdateTag): Promise<TagStaff> {
    const __result = await this.api.updateTagStaff(source);
    this.tagStaffCache.clear();
    return __result
  }
  async updateBoatTypeStaff(source: UpdateBoatType): Promise<BoatTypeStaff> {
    const __result = await this.api.updateBoatTypeStaff(source);
    this.boatTypeStaffCache.clear();
    return __result
  }
  async updateCountryStaff(source: UpdateCountry): Promise<CountryStaff> {
    const __result = await this.api.updateCountryStaff(source);
    this.countryStaffCache.clear();
    return __result
  }
  async updateAmericanStateStaff(source: UpdateAmericanState): Promise<AmericanStateStaff> {
    const __result = await this.api.updateAmericanStateStaff(source);
    this.americanStateStaffCache.clear();
    return __result
  }
  async updateSwimProofMethodStaff(source: UpdateSwimProofMethod): Promise<SwimProofMethodStaff> {
    const __result = await this.api.updateSwimProofMethodStaff(source);
    this.swimProofMethodStaffCache.clear();
    return __result
  }
  async updateMembershipTypeDiscountStaff(source: UpdateMembershipTypeDiscount): Promise<MembershipTypeDiscountStaff> {
    const __result = await this.api.updateMembershipTypeDiscountStaff(source);
    this.membershipTypeDiscountStaffCache.clear();
    return __result
  }
  async deletePersonTypeStaff(source: DeletePersonTypeStaff): Promise<void> {
    const __result = await this.api.deletePersonTypeStaff(source);
    this.personTypeStaffCache.clear();
    return __result
  }
  async deleteTagStaff(source: DeleteTagStaff): Promise<void> {
    const __result = await this.api.deleteTagStaff(source);
    this.tagStaffCache.clear();
    return __result
  }
  async deleteBoatTypeStaff(source: DeleteBoatTypeStaff): Promise<void> {
    const __result = await this.api.deleteBoatTypeStaff(source);
    this.boatTypeStaffCache.clear();
    return __result
  }
  async deleteCountryStaff(source: DeleteCountryStaff): Promise<void> {
    const __result = await this.api.deleteCountryStaff(source);
    this.countryStaffCache.clear();
    return __result
  }
  async deleteAmericanStateStaff(source: DeleteAmericanStateStaff): Promise<void> {
    const __result = await this.api.deleteAmericanStateStaff(source);
    this.americanStateStaffCache.clear();
    return __result
  }
  async deleteSwimProofMethodStaff(source: DeleteSwimProofMethodStaff): Promise<void> {
    const __result = await this.api.deleteSwimProofMethodStaff(source);
    this.swimProofMethodStaffCache.clear();
    return __result
  }
  async deleteMembershipTypeDiscountStaff(source: DeleteMembershipTypeDiscountStaff): Promise<void> {
    const __result = await this.api.deleteMembershipTypeDiscountStaff(source);
    this.membershipTypeDiscountStaffCache.clear();
    return __result
  }
  async searchTagStaff(params: TagSearchParams, page: number, perPage: number): Promise<PaginationResult<TagStaffSearchResult>> {
    return this.api.searchTagStaff(params, page, perPage)
  }
  async searchTagStaffFull(params: TagSearchParams, page: number, perPage: number): Promise<PaginationResult<TagStaff>> {
    return this.api.searchTagStaffFull(params, page, perPage)
  }
  async getMembershipTypeDiscountStaffMembershipTypeId(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff | null> {
    return this.api.getMembershipTypeDiscountStaffMembershipTypeId(id, options)
  }
}
